import { defaultConfig } from 'app/config/environment';
import Axios from 'axios';
import { useTranslate } from 'core/hooks/useTranslate';
import { successResponse } from 'core/utils/utils';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import consumptionsv2 from '../../../../store/viewer';
import { Icon } from 'ui';

const VideoPlayer = ({
  guid,
  contentUrl,
  content_guid,
  content_url,
  contentName,
  contentDescription,
  thumbnail,
  userGuid,
  measureProgress,
  jobStatus,
  updateJob,
  pauseContent,
}) => {
  const { t } = useTranslate();
  const [trackProgress, setTrackProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [consumptionGuid, setConsumptionGuid] = useState('');
  const [consumptionSessionGuid, setConsumptionSessionGuid] = useState('');
  const [isReady, setIsReady] = useState(false);
  const [videoContentUrl, setVideoContentUrl] = useState(content_url);
  const [videoContentType, setVideoContentType] = useState('');
  const [hasVideoError, setHasVideoError] = useState(false);
  const [showFallback, setShowFallback] = useState(false);

  // Refs
  let videoRef = useRef();

  useEffect(() => {
    if (pauseContent && videoRef.current) {
      videoRef.current.pause();
    }
  }, [pauseContent]);

  useEffect(() => {
    if (guid && duration && measureProgress) {
      consumptionsv2(
        {
          type: 'INIT_SESION_CONSUMPTION',
          params: {
            guid: null,
            person_guid: userGuid,
            content_guid: guid,
            status: 'inProgress',
            type: 'video',
            progress: trackProgress,
            progress_ends: duration,
          },
        },
        (result) => {
          setConsumptionGuid(result.guid);

          if (videoRef && videoRef.current) videoRef.current.currentTime = result.last || 0;
          setConsumptionSessionGuid(result.session_guid);

          if ((result.status === 'finished' || result.progress > 0.8) && !jobStatus) {
            updateJob(guid, true);
          }
        }
      );
    }
  }, [guid, duration]);

  useEffect(() => {
    if (!videoContentUrl) return;

    let tempVideoType = videoContentUrl.split('.').pop();
    tempVideoType = tempVideoType.indexOf('?') > 0 ? tempVideoType.split('?')[0] : tempVideoType;

    setVideoContentType(`video/${tempVideoType}`);

    // Para archivos .mov, verificar si realmente no se puede cargar
    if (tempVideoType.toLowerCase() === 'mov') {
      const timeoutId = setTimeout(() => {
        // Solo mostrar fallback si el video NO se ha cargado exitosamente
        if (videoRef.current && videoRef.current.readyState < 2) {
          setShowFallback(true);
        }
      }, 3000); // Aumentar a 3 segundos para dar más tiempo

      // Limpiar timeout si el componente se desmonta
      return () => clearTimeout(timeoutId);
    }

    if (videoContentUrl.startsWith('https://')) return;

    getPublicUrl();
  }, [videoContentUrl]);

  const getPublicUrl = async () => {
    let jsonDataResponse = await Axios.get(defaultConfig.BASE_URL_ + videoContentUrl.substring(1));
    if (successResponse(jsonDataResponse)) {
      let responseCookies = jsonDataResponse.data.data.cookies;
      let _file_cookies = [];
      responseCookies.forEach((cookie) => {
        document.cookie = cookie;
        _file_cookies.push(cookie);
      });
      setVideoContentUrl(jsonDataResponse.data.data.url);
    }
  };

  useEffect(() => {
    if (consumptionGuid && consumptionGuid.length > 0) {
      setIsReady(true);
    }
  }, [consumptionGuid]);

  useEffect(() => {
    if (consumptionGuid) {
      consumptionsv2(
        {
          type: 'UPDATE_CONSUMPTION',
          params: {
            id: 0,
            consumption_guid: consumptionGuid,
            content_guid: guid,
            session_guid: consumptionSessionGuid,
            progress_start: trackProgress - 1,
            progress_end: trackProgress,
            started_at: new Date() - 1000,
            ended_at: Date.now(),
          },
        },
        (result) => {
          if (measureProgress && result.progress > 0.8 && (!jobStatus || jobStatus === 'pending')) {
            updateJob(guid, true);
          }
        }
      );
    }
  }, [trackProgress]);

  const handleVideoError = (e) => {
    console.error('Video playback error:', e);
    setHasVideoError(true);
    setShowFallback(true);
  };

  const handleVideoCanPlay = () => {
    // Si el video se puede reproducir, asegurar que no se muestre fallback
    setHasVideoError(false);
    setShowFallback(false);
  };

  const getDownloadUrl = () => {
    return contentUrl || videoContentUrl;
  };

  const getFileName = () => {
    const url = getDownloadUrl();
    const fileName = url.split('/').pop().split('?')[0];
    return fileName || contentName || 'video';
  };

  return (
    <div className="preview-content__video">
      {showFallback && (
        <div className="preview-content__video-fallback">
          <div className="preview-content__error-message">
            <p>{t('messages:The format of this video is not supported in your browser')}</p>
            <a href={getDownloadUrl()} download={getFileName()} className="preview-content__download-link" aria-label={t('buttons:You can download it here')}>
              <Icon name="download" /> <span>{t('buttons:You can download it here')}</span>
            </a>
          </div>
        </div>
      )}
      <video
        ref={videoRef}
        key={videoContentUrl || ''}
        poster={thumbnail || undefined}
        width="100%"
        controls
        controlsList="nodownload"
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onTimeUpdate={(e) => {
          if (Math.abs(e.target.currentTime - trackProgress) > 1) {
            setTrackProgress(e.target.currentTime);
          }
        }}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onError={handleVideoError}
        onCanPlay={handleVideoCanPlay}
        style={{ display: showFallback ? 'none' : 'block' }}
      >
        <source src={videoContentUrl} type={videoContentType} />
        {t('messages:Browser not supported message')}{' '}
        <a href={contentUrl} target="_blank" rel="noopener noreferrer" aria-label={t('buttons:Open video in new tab')}>
          {t('messages:Video link text')}
        </a>{' '}
        {t('messages:Instead message')}.
      </video>
      <div className="preview-content__type-with-icon-text-container">
        {/* {contentName && <span className="preview-content__name">{contentName}</span>} */}
        <span className="preview-content__description">{contentDescription}</span>
      </div>
    </div>
  );
};

VideoPlayer.propTypes = {
  onShowVisor: PropTypes.func,
  measureProgress: PropTypes.bool,
  thumbnail: PropTypes.string,
};

VideoPlayer.defaultProps = {
  onShowVisor: function () {
    return null;
  },
  measureProgress: true,
  contentUrl: '',
  updateJob: function () {
    return null;
  },
};

export default VideoPlayer;
